/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { isToday } from 'date-fns';
import { useRef } from 'react';

import { ExecutionLine } from './ExecutionLine';

import { IWithPagination } from '@/commons/commons';
import { RunExecutionDetails } from '@/commons/protocols';

interface ExecutionSelectorProps {
    executions: RunExecutionDetails[];
    onSelect: (id: string) => void;
    currentExecutionId?: string;
    pagination: IWithPagination<RunExecutionDetails>['pagination'];
    onLoadMore: () => void;
}

export const ExecutionSelector = ({
    executions,
    onSelect,
    currentExecutionId,
    pagination,
    onLoadMore,
}: ExecutionSelectorProps) => {
    const observerTarget = useRef<HTMLDivElement>(null);

    const { todayExecutions, previousExecutions } = executions.reduce(
        (acc, execution) => {
            if (isToday(new Date(execution.createdAt))) {
                acc.todayExecutions.push(execution);
            } else {
                acc.previousExecutions.push(execution);
            }
            return acc;
        },
        { todayExecutions: [], previousExecutions: [] } as {
            todayExecutions: RunExecutionDetails[];
            previousExecutions: RunExecutionDetails[];
        },
    );

    return (
        <div className="flex flex-col gap-4 h-full">
            <div className="flex flex-col gap-2 overflow-y-auto">
                {executions.length === 0 && <div className="py-8 text-center text-slate-500">No executions found</div>}

                {todayExecutions.length > 0 && (
                    <>
                        <h4 className="text-black font-400 text-[18px] pb-[8px] border-b">Today's runs</h4>
                        {todayExecutions.map((execution, index) => (
                            <ExecutionLine
                                key={execution.id}
                                execution={execution}
                                onSelect={onSelect}
                                isSelected={execution.id.toString() === currentExecutionId}
                                index={index}
                            />
                        ))}
                    </>
                )}

                {previousExecutions.length > 0 && (
                    <>
                        {todayExecutions.length > 0 && (
                            <h4 className="text-black font-400 text-[18px] pb-[8px] border-b">Previous runs</h4>
                        )}
                        {previousExecutions.map((execution, index) => (
                            <ExecutionLine
                                key={execution.id}
                                execution={execution}
                                onSelect={onSelect}
                                isSelected={execution.id.toString() === currentExecutionId}
                                index={index}
                            />
                        ))}
                    </>
                )}

                <div ref={observerTarget} className="h-4" />
            </div>

            {pagination && pagination.nextPage && (
                <div className="mt-auto pt-4 text-body flex items-center justify-center gap-[16px]">
                    {pagination.total && (
                        <span className="text-slate-500">{`${pagination.total - executions.length} more runs`}</span>
                    )}
                    <button className="text-blue-600" onClick={onLoadMore}>
                        Load 20 more
                    </button>
                </div>
            )}
        </div>
    );
};
